// KaziPeople Web — Privacy & Security. The feature's thesis is honesty and NO
// browsing surveillance, so this page states plainly what is and isn't collected.
// Every claim here is true of the Phase 1 implementation: recents are localStorage
// only (recents.ts), classification is server-authoritative with zero server-side
// fetching of user URLs (no SSRF surface), and the only server-stored browser data is
// company bookmarks + the tenant URL policy (browser.service). Keep this page in sync
// with the implementation — it is a promise, not marketing.
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Shield, EyeOff, HardDrive, Server, Building2, Globe, Lock, ArrowLeft,
  Check, X, Settings,
} from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';

const Promise_: React.FC<{ kind: 'do' | 'dont'; children: React.ReactNode }> = ({ kind, children }) => (
  <li className="flex items-start gap-2.5">
    <span
      className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full ${
        kind === 'do' ? 'bg-success-soft text-success-strong' : 'bg-danger-soft text-danger'
      }`}
    >
      {kind === 'do' ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
    </span>
    <span className="text-sm text-foreground">{children}</span>
  </li>
);

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <section className="rounded-xl border border-border bg-card p-5">
    <div className="mb-3 flex items-center gap-2.5">
      <div className="rounded-lg bg-muted p-1.5 text-muted-foreground">{icon}</div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
    </div>
    {children}
  </section>
);

export const WebPrivacyPage: React.FC = () => {
  const { t } = useTranslation('webBrowser');
  const navigate = useNavigate();
  const loc = useLocation();
  const backToWeb = () => navigate(loc.pathname.replace(/\/web\/privacy$/, '/web'));

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-500/20">
            <Shield className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('privacy.title', { defaultValue: 'Privacy & security' })}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('privacy.subtitle', { defaultValue: 'How KaziPeople Web handles the sites you open — in plain terms.' })}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={backToWeb}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> {t('privacy.back', { defaultValue: 'Back to Web' })}
        </Button>
      </div>

      {/* What it is */}
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="text-sm leading-relaxed text-foreground">
          {t('privacy.intro', {
            defaultValue:
              'KaziPeople Web launches, honestly represents, and connects external websites to your work. It is not a full web browser, it does not embed arbitrary sites, and it is not a way to watch what you browse. When you open a site, it opens in your own browser — KaziPeople stays out of the way.',
          })}
        </p>
      </div>

      {/* No surveillance */}
      <Section icon={<EyeOff className="h-4 w-4" />} title={t('privacy.no_surveillance_title', { defaultValue: 'No browsing surveillance' })}>
        <ul className="space-y-2.5">
          <Promise_ kind="dont">
            {t('privacy.ns1', { defaultValue: 'KaziPeople does not keep a history of the external sites you open. There is no central log of your browsing.' })}
          </Promise_>
          <Promise_ kind="dont">
            {t('privacy.ns2', { defaultValue: 'KaziPeople servers never fetch, render, or store the contents of the pages you visit — the address you enter is classified for safety, and that’s all.' })}
          </Promise_>
          <Promise_ kind="dont">
            {t('privacy.ns3', { defaultValue: 'Your managers and admins cannot see your recent resources or which sites you personally opened.' })}
          </Promise_>
          <Promise_ kind="do">
            {t('privacy.ns4', { defaultValue: 'When you open a link, it opens directly in your browser with no tracking added by KaziPeople.' })}
          </Promise_>
        </ul>
      </Section>

      {/* Stays on your device */}
      <Section icon={<HardDrive className="h-4 w-4" />} title={t('privacy.device_title', { defaultValue: 'What stays on your device only' })}>
        <ul className="space-y-2.5">
          <Promise_ kind="do">
            {t('privacy.dev1', { defaultValue: 'Your “Recent work resources” list lives only in this browser (local storage). It is never sent to a server or shared with anyone.' })}
          </Promise_>
          <Promise_ kind="do">
            {t('privacy.dev2', { defaultValue: 'Clearing your recents (the Clear button on the Web home) removes them immediately, on this device.' })}
          </Promise_>
          <Promise_ kind="do">
            {t('privacy.dev3', { defaultValue: 'Signing in on another device starts fresh — recents do not follow you, because they were never stored centrally.' })}
          </Promise_>
        </ul>
      </Section>

      {/* Honest representation */}
      <Section icon={<Lock className="h-4 w-4" />} title={t('privacy.honest_title', { defaultValue: 'Honest, anti-phishing representation' })}>
        <ul className="space-y-2.5">
          <Promise_ kind="do">
            {t('privacy.h1', { defaultValue: 'Every address is checked on the server and shown with its real domain — so you always see where a link actually goes.' })}
          </Promise_>
          <Promise_ kind="do">
            {t('privacy.h2', { defaultValue: 'KaziPeople Web never fakes an embedded page. If a site opens in your browser, it says so plainly.' })}
          </Promise_>
          <Promise_ kind="do">
            {t('privacy.h3', { defaultValue: 'Unsafe addresses (credentials in the URL, non-web schemes) are flagged and not opened silently.' })}
          </Promise_>
          <Promise_ kind="do">
            {t('privacy.h4', { defaultValue: 'External links open with protections that stop the opened page from controlling this tab.' })}
          </Promise_>
        </ul>
      </Section>

      {/* Company controls */}
      <Section icon={<Building2 className="h-4 w-4" />} title={t('privacy.company_title', { defaultValue: 'What your company controls' })}>
        <ul className="space-y-2.5">
          <Promise_ kind="do">
            {t('privacy.c1', { defaultValue: 'Your company can allow or block specific domains and decide whether external sites and downloads are permitted.' })}
          </Promise_>
          <Promise_ kind="do">
            {t('privacy.c2', { defaultValue: 'Admins curate the shared company bookmarks everyone sees on the Web home.' })}
          </Promise_>
          <Promise_ kind="dont">
            {t('privacy.c3', { defaultValue: 'Admins manage policy and bookmarks — not a record of your personal browsing. There is no such record to manage.' })}
          </Promise_>
        </ul>
      </Section>

      {/* Data table */}
      <Section icon={<Server className="h-4 w-4" />} title={t('privacy.data_title', { defaultValue: 'What is stored, and where' })}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">{t('privacy.tbl_data', { defaultValue: 'Data' })}</th>
                <th className="py-2 pr-4 font-medium">{t('privacy.tbl_where', { defaultValue: 'Where it lives' })}</th>
                <th className="py-2 font-medium">{t('privacy.tbl_who', { defaultValue: 'Who can see it' })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr>
                <td className="py-2.5 pr-4 text-foreground">{t('privacy.row_bookmarks', { defaultValue: 'Company bookmarks' })}</td>
                <td className="py-2.5 pr-4 text-muted-foreground">{t('privacy.loc_server', { defaultValue: 'KaziPeople (your company)' })}</td>
                <td className="py-2.5 text-muted-foreground">{t('privacy.who_team', { defaultValue: 'Your team; managed by admins' })}</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 text-foreground">{t('privacy.row_policy', { defaultValue: 'External-browsing policy' })}</td>
                <td className="py-2.5 pr-4 text-muted-foreground">{t('privacy.loc_server', { defaultValue: 'KaziPeople (your company)' })}</td>
                <td className="py-2.5 text-muted-foreground">{t('privacy.who_admins', { defaultValue: 'Admins' })}</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 text-foreground">{t('privacy.row_recents', { defaultValue: 'Your recent resources' })}</td>
                <td className="py-2.5 pr-4 text-muted-foreground">{t('privacy.loc_device', { defaultValue: 'This device only (local storage)' })}</td>
                <td className="py-2.5 text-muted-foreground">{t('privacy.who_you', { defaultValue: 'Only you' })}</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 text-foreground">{t('privacy.row_history', { defaultValue: 'Your browsing history / page contents' })}</td>
                <td className="py-2.5 pr-4 text-muted-foreground">{t('privacy.loc_none', { defaultValue: 'Not stored by KaziPeople' })}</td>
                <td className="py-2.5 text-muted-foreground">{t('privacy.who_noone', { defaultValue: 'No one — it isn’t collected' })}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Access */}
      <Section icon={<Globe className="h-4 w-4" />} title={t('privacy.access_title', { defaultValue: 'Access & controls' })}>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t('privacy.access_body', {
            defaultValue:
              'KaziPeople Web is available only to companies that have enabled it, and within your company, access follows your role. Managing bookmarks and policy requires the browser management permission or a company-admin role. Admins can adjust these settings on the Manage page.',
          })}
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate(loc.pathname.replace(/\/web\/privacy$/, '/settings/web'))}>
          <Settings className="mr-1.5 h-4 w-4" /> {t('privacy.open_settings', { defaultValue: 'Open Web settings' })}
        </Button>
      </Section>

      <p className="pb-2 text-center text-xs text-muted-foreground">
        {t('privacy.footer', { defaultValue: 'This describes KaziPeople Web (Phase 1). Embedded previews and richer features arrive in later phases with their own, clearly stated protections.' })}
      </p>
    </div>
  );
};

export default WebPrivacyPage;
